import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { extractStoragePath, getSignedStorageUrl } from '@/utils/storageUtils';
import { toast } from 'sonner';

const BRANDING_BUCKET = 'branding';

/** Branding images are stored as raw bucket paths and re-signed on read. */
const resolveBrandingUrl = async (value: string | null | undefined): Promise<string | null> => {
  if (!value) return null;
  if (value.startsWith('data:') || value.startsWith('blob:')) return value;
  const path = extractStoragePath(BRANDING_BUCKET, value) ?? extractStoragePath('documents', value);
  if (!path) return value.startsWith('http') ? value : null;
  try {
    return await getSignedStorageUrl(BRANDING_BUCKET, path, 86400);
  } catch {
    return null;
  }
};


export interface BrandingSettings {
  id: string;
  organization_name: string;
  logo_url: string | null;
  login_banner_url: string | null;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  header_mode: 'light' | 'dark';
}

export const useBranding = () => {
  const queryClient = useQueryClient();

  const { data: branding, isLoading } = useQuery({
    queryKey: ['branding'],
    queryFn: async () => {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData?.user) return null;

      const { data: member } = await supabase
        .from('organization_members')
        .select('organization_id')
        .eq('user_id', userData.user.id)
        .maybeSingle();

      const orgId = member?.organization_id;
      if (!orgId) return null;

      const { data, error } = await supabase
        .from('branding_settings')
        .select('*')
        .eq('organization_id', orgId)
        .maybeSingle();

      if (error) throw error;

      if (!data) {
        return {
          id: '',
          organization_name: 'Cimfoni Management',
          logo_url: null,
          login_banner_url: null,
          primary_color: '#2563eb',
          secondary_color: '#eff6ff',
          accent_color: '#dbeafe',
          header_mode: 'light' as const,
        } as BrandingSettings;
      }

      const [logo_url, login_banner_url] = await Promise.all([
        resolveBrandingUrl((data as any).logo_url),
        resolveBrandingUrl((data as any).login_banner_url),
      ]);

      return { ...(data as any), logo_url, login_banner_url } as BrandingSettings;
    }
  });

  /** Uploads to the private branding bucket and returns the raw storage path. */
  const uploadBrandingImage = async (file: File, type: 'logo' | 'banner'): Promise<string | null> => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData?.user) throw new Error('Not authenticated');

      const { data: member } = await supabase
        .from('organization_members')
        .select('organization_id')
        .eq('user_id', userData.user.id)
        .maybeSingle();

      const orgId = member?.organization_id;
      if (!orgId) throw new Error('No organization found for user');

      const fileExt = (file.name.split('.').pop() || 'png').toLowerCase();
      const filePath = `${orgId}/${type}_${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from(BRANDING_BUCKET)
        .upload(filePath, file, { upsert: true, contentType: file.type || undefined });

      if (uploadError) throw uploadError;

      return filePath;

    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
      return null;
    }
  };

  const updateBranding = useMutation({
    mutationFn: async (updates: Partial<BrandingSettings>) => {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData?.user) throw new Error('Not authenticated');

      const { data: member } = await supabase
        .from('organization_members')
        .select('organization_id')
        .eq('user_id', userData.user.id)
        .maybeSingle();

      const orgId = member?.organization_id;
      if (!orgId) throw new Error('No organization found for user');

      const { data: existing } = await supabase
        .from('branding_settings')
        .select('id')
        .eq('organization_id', orgId)
        .maybeSingle();

      if (existing) {
        const { data, error } = await supabase
          .from('branding_settings')
          .update(updates)
          .eq('id', existing.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        const { data, error } = await supabase
          .from('branding_settings')
          .insert({ ...updates, organization_id: orgId })
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branding'] });
      toast.success('Branding updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update branding');
    }
  });


  return {
    branding,
    isLoading,
    updateBranding: updateBranding.mutate,
    uploadBrandingImage,
    isUpdating: updateBranding.isPending
  };
};
